import { css } from "@emotion/css";
import { useLabel } from "react-aria";
import { SelectProps as AriaSelectProps, Button } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { _ListBox } from "./assets/_ListBox";
import { _Select } from "./assets/_Select";
import { _SelectValue } from "./assets/_SelectValue";
import { Icon } from "../../../foundations/icons/Icon/Icon";
import { _Popover } from "../../_assets/_Popover";
import { _Text } from "../../_assets/_Text";
import { Divider } from "../../divider/Divider";
import {
  FieldErrorsProps,
  FieldErrors,
} from "../_assets/input-wrapper/FieldErrors";
import { FormLabelRAC } from "../_assets/input-wrapper/FormLabelRAC";

export interface SelectPropsRAC<T extends object>
  extends Omit<AriaSelectProps<T>, "children"> {
  label?: string;
  description?: string;
  errors?: FieldErrorsProps["children"];
  emptyMessage?: string;
  isLoading?: boolean;
  items?: Iterable<T>;
  info?: React.ReactNode;
  children: React.ReactNode | ((item: T) => React.ReactNode);
  footer?: React.ReactNode;
  testID: string;
}

export function SelectRAC<T extends object>(props: SelectPropsRAC<T>) {
  const { t } = useTranslation();
  const { labelProps, fieldProps } = useLabel(props);

  const {
    label,
    description,
    placeholder = t("Please select"),
    errors,
    emptyMessage = t("No results found"),
    isLoading,
    items,
    children,
    footer,
    testID,
    ...selectProps
  } = props;
  const isEmpty =
    (!items && !children) || (items && items && [...items].length < 1);

  return (
    <div className={styles.wrapper}>
      {props.label && (
        <FormLabelRAC
          isRequired={props.isRequired}
          info={props.info}
          {...labelProps}
        >
          {props.label}
        </FormLabelRAC>
      )}
      <_Select
        {...selectProps}
        {...fieldProps}
        onBlur={props.onBlur}
        placeholder={isEmpty ? emptyMessage : placeholder}
        isDisabled={isEmpty ? true : selectProps.isDisabled}
      >
        <Button data-testid={testID}>
          <_SelectValue isLoading={isLoading} />
          <Icon name="expand_more" aria-hidden />
        </Button>

        {description && (
          <_Text style="input/helper" color="text-weak" slot="description">
            {description}
          </_Text>
        )}
        <FieldErrors>{errors}</FieldErrors>
        <_Popover offset={8} isLoading={isLoading}>
          <_ListBox items={items}>{children}</_ListBox>
          {footer && <Divider />}
          {footer}
        </_Popover>
      </_Select>
    </div>
  );
}

const styles = {
  wrapper: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
};
